import type { JSX } from 'react';
import type { ListDto } from '@wishare/shared';
import { firstGrapheme, hueFromTitle } from '../lib/cover.js';

/**
 * Мозаика обложек первых желаний списка.
 *
 * Желания с фотографией показываются снимком, без фотографии — той же
 * обложкой, что и в карточке желания (`lib/cover.ts`): один расчёт на оба
 * места, иначе цвета разъедутся при первой правке.
 *
 * Пустой список получает не пустоту, а свой смайлик крупно: «здесь пока
 * ничего» должно читаться как приглашение, а не как поломка.
 */
export function ListMosaic({ list }: { list: ListDto }): JSX.Element {
  const preview = list.preview.slice(0, 4);
  const emoji = list.coverEmoji || '🎁';

  /*
   * Сколько желаний осталось за плитками.
   *
   * Без этого числа список из сорока желаний выглядел ровно как список из
   * четырёх: сколько внутри, написано подписью под карточкой, но глаз читает
   * картинку, а не подпись под ней.
   *
   * Разность честная, а не «примерно»: и `itemCount`, и превью считаются на
   * сервере одним условием `is_archived = 0` (domain/serialize.ts) — из
   * четырёх плиток вычитается ровно то, что в них показано.
   */
  const скрыто = Math.max(0, list.itemCount - preview.length);

  if (preview.length === 0) {
    return (
      <span className="list-card__mosaic list-card__mosaic--empty" aria-hidden="true">
        <span className="list-card__emoji list-card__emoji--big">{emoji}</span>
      </span>
    );
  }

  return (
    <span className={`list-card__mosaic list-card__mosaic--${preview.length}`} aria-hidden="true">
      {preview.map((entry, index) => (
        <span
          key={`${entry.title}-${index}`}
          className="list-card__tile"
          style={{ ['--cover-hue' as string]: String(hueFromTitle(entry.title)) }}
        >
          {entry.imageUrl ? (
            <img src={entry.imageUrl} alt="" loading="lazy" />
          ) : (
            <span className="list-card__letter">{firstGrapheme(entry.title)}</span>
          )}
          {/*
            Метка идёт на ПОСЛЕДНЮЮ плитку: на первой она закрыла бы то, с чего
            человек начинает читать мозаику.
          */}
          {index === preview.length - 1 && скрыто > 0 && (
            <span className="list-card__more-count">+{скрыто}</span>
          )}
        </span>
      ))}
      <span className="list-card__emoji">{emoji}</span>
    </span>
  );
}
